import { useEffect, useMemo, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import type { AiKnowledgeBaseEntryDto } from '@/shared/api/ai-knowledge-base';
import { Loader } from '@/shared/ui/Loader';
import { Notification } from '@/shared/ui';

import {
  useAiKnowledgeBaseEntries,
  useCreateAiKnowledgeBaseEntry,
  useDeleteAiKnowledgeBaseEntry,
  useUpdateAiKnowledgeBaseEntry,
} from '../model/useAiKnowledgeBase';

function parseTags(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 10);
}

function formatTags(tags: string[]): string {
  return tags.join(', ');
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function KnowledgeBaseEntryRow({
  companyId,
  entry,
  accentColor,
  disabled,
}: {
  companyId: string;
  entry: AiKnowledgeBaseEntryDto;
  accentColor: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('company');
  const updateMutation = useUpdateAiKnowledgeBaseEntry(companyId);
  const deleteMutation = useDeleteAiKnowledgeBaseEntry(companyId);
  const [title, setTitle] = useState(entry.title);
  const [content, setContent] = useState(entry.content);
  const [tagsText, setTagsText] = useState(formatTags(entry.tags));
  const [enabled, setEnabled] = useState(entry.enabled);

  useEffect(() => {
    setTitle(entry.title);
    setContent(entry.content);
    setTagsText(formatTags(entry.tags));
    setEnabled(entry.enabled);
  }, [entry]);

  const tags = useMemo(() => parseTags(tagsText), [tagsText]);
  const isBusy = updateMutation.isPending || deleteMutation.isPending;
  const isDirty =
    title !== entry.title ||
    content !== entry.content ||
    tagsText !== formatTags(entry.tags) ||
    enabled !== entry.enabled;
  const canSave = !disabled && !isBusy && isDirty && title.trim() !== '' && content.trim() !== '';

  return (
    <Box
      sx={{
        p: 1.25,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        >
          <TextField
            size="small"
            label={t('modules.knowledgeBase.fields.title')}
            value={title}
            disabled={disabled || isBusy}
            onChange={(event) => setTitle(event.target.value)}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
          <FormControlLabel
            sx={{
              m: 0,
              minHeight: 40,
              alignSelf: { xs: 'flex-start', sm: 'center' },
              '& .MuiFormControlLabel-label': {
                fontSize: 14,
                lineHeight: 1.35,
              },
            }}
            control={(
              <Switch
                size="small"
                checked={enabled}
                disabled={disabled || isBusy}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            )}
            label={
              enabled
                ? t('modules.knowledgeBase.enabled')
                : t('modules.knowledgeBase.disabled')
            }
          />
        </Stack>

        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('modules.knowledgeBase.fields.content')}
          value={content}
          disabled={disabled || isBusy}
          onChange={(event) => setContent(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />

        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.tags')}
          value={tagsText}
          disabled={disabled || isBusy}
          helperText={t('modules.knowledgeBase.tagsHint')}
          onChange={(event) => setTagsText(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
            '& .MuiFormHelperText-root': {
              color: 'text.secondary',
            },
          }}
        />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={formatDateTime(entry.updated_at)}
              sx={{
                borderRadius: 1,
                color: 'text.secondary',
              }}
            />
            {tags.map((tag) => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                sx={{
                  borderRadius: 1,
                  bgcolor: alpha(accentColor, 0.08),
                  color: accentColor,
                }}
              />
            ))}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
          >
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              disabled={disabled || isBusy}
              onClick={() => {
                if (window.confirm(t('modules.knowledgeBase.deleteConfirm'))) {
                  deleteMutation.mutate(entry.id);
                }
              }}
              sx={{
                minHeight: 34,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 800,
              }}
            >
              {t('modules.knowledgeBase.delete')}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveOutlinedIcon fontSize="small" />}
              disabled={!canSave}
              onClick={() => {
                updateMutation.mutate({
                  id: entry.id,
                  data: {
                    title: title.trim(),
                    content: content.trim(),
                    tags,
                    enabled,
                  },
                });
              }}
              sx={{
                minHeight: 34,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 800,
              }}
            >
              {t('modules.knowledgeBase.save')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Notification
        open={updateMutation.isError || deleteMutation.isError}
        title={updateMutation.error?.message || deleteMutation.error?.message}
      />
    </Box>
  );
}

export function KnowledgeBasePanel({
  companyId,
  accentColor,
  disabled,
}: {
  companyId: string;
  accentColor: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('company');
  const entriesQuery = useAiKnowledgeBaseEntries(companyId);
  const createMutation = useCreateAiKnowledgeBaseEntry(companyId);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [enabled, setEnabled] = useState(true);
  const tags = useMemo(() => parseTags(tagsText), [tagsText]);
  const entries = entriesQuery.data?.entries ?? [];
  const total = entriesQuery.data?.total ?? entries.length;
  const canCreate =
    !disabled &&
    !createMutation.isPending &&
    title.trim() !== '' &&
    content.trim() !== '';

  if (entriesQuery.isPending) return <Loader containerSize="small" />;

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: 'text.primary' }}
          >
            {t('modules.knowledgeBase.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary' }}
          >
            {t('modules.knowledgeBase.caption')}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={t('modules.knowledgeBase.count', { count: total })}
          sx={{
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: alpha(accentColor, 0.1),
            color: accentColor,
            border: '1px solid',
            borderColor: alpha(accentColor, 0.22),
          }}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(180px, 280px) minmax(0, 1fr)' },
          gap: 1.25,
        }}
      >
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.title')}
          value={title}
          disabled={disabled || createMutation.isPending}
          onChange={(event) => setTitle(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.tags')}
          value={tagsText}
          disabled={disabled || createMutation.isPending}
          helperText={t('modules.knowledgeBase.tagsHint')}
          onChange={(event) => setTagsText(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
            '& .MuiFormHelperText-root': {
              color: 'text.secondary',
            },
          }}
        />
        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('modules.knowledgeBase.fields.content')}
          value={content}
          disabled={disabled || createMutation.isPending}
          onChange={(event) => setContent(event.target.value)}
          sx={{
            gridColumn: '1 / -1',
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <FormControlLabel
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: 14,
              lineHeight: 1.35,
            },
          }}
          control={(
            <Switch
              size="small"
              checked={enabled}
              disabled={disabled || createMutation.isPending}
              onChange={(event) => setEnabled(event.target.checked)}
            />
          )}
          label={
            enabled
              ? t('modules.knowledgeBase.enabled')
              : t('modules.knowledgeBase.disabled')
          }
        />

        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon fontSize="small" />}
          disabled={!canCreate}
          onClick={() => {
            createMutation.mutate({
              title: title.trim(),
              content: content.trim(),
              tags,
              enabled,
            }, {
              onSuccess: () => {
                setTitle('');
                setContent('');
                setTagsText('');
                setEnabled(true);
              },
            });
          }}
          sx={{
            minWidth: { xs: '100%', sm: 220 },
            minHeight: 38,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          {t('modules.knowledgeBase.add')}
        </Button>
      </Stack>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('modules.knowledgeBase.empty')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {entries.map((entry) => (
            <KnowledgeBaseEntryRow
              key={entry.id}
              companyId={companyId}
              entry={entry}
              accentColor={accentColor}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}

      <Notification
        open={entriesQuery.isError || createMutation.isError}
        title={entriesQuery.error?.message || createMutation.error?.message}
      />
    </Stack>
  );
}
